import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "employee__timeblocks"
}

import { computed, ref, watch, nextTick, onMounted } from 'vue';
import { useStore } from 'vuex';
import AEmployeeSelect from './a-employee-select.vue';
import AEmployeeTimeblock from './a-employee-timeblock.vue';
import type {
  EmployeeData,
  TimeblockData,
  CustomFilterFunction,
} from './types';

interface Props {
  employeeData: EmployeeData;
  timeblocks?: TimeblockData[];
  allTimeblocks?: TimeblockData[]; // All timeblocks from all employees
  deletedTimeblockIds?: number[]; // IDs of saved timeblocks that have been deleted
  draftTimeblocks?: TimeblockData[]; // Draft timeblocks from all employees in draft subtask
  customFilter?: CustomFilterFunction;
  hasBackground?: boolean;
  required?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'a-employee',
  props: {
    employeeData: {},
    timeblocks: { default: () => [] },
    allTimeblocks: { default: () => [] },
    deletedTimeblockIds: { default: () => [] },
    draftTimeblocks: { default: () => [] },
    customFilter: { type: Function, default: undefined },
    hasBackground: { type: Boolean, default: true },
    required: { type: Boolean, default: false }
  },
  emits: ["update:employeeData", "update:timeblocks", "clearEmployee", "addTimeblock"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();

// Local models with v-model sync
const employeeDataLocal = computed({
  get: () => props.employeeData,
  set: (value: EmployeeData) => emit('update:employeeData', value),
});

// Refs for validation
const employeeSelectRef = ref<InstanceType<typeof AEmployeeSelect> | null>(
  null,
);
const timeblockRefs = ref<any[]>([]);

const setTimeblockRef = (el: any, index: number) => {
  if (el) {
    timeblockRefs.value[index] = el;
  }
};

// Get the full employee object from store
const selectedEmployee = computed(() => {
  if (!props.employeeData.id) return null;
  const employees = store.getters['tm/enrichedEmployees'];
  return employees.find((emp: any) => emp.id == props.employeeData.id) || null;
});

// Filter allTimeblocks to only include timeblocks for current employee
const allTimeblocksForCurrentEmployee = computed(() => {
  if (!props.employeeData.id) return [];
  return props.allTimeblocks.filter(
    (tb) => tb.employee_id == props.employeeData.id,
  );
});

// Watch for when employee is selected and ensure at least one timeblock exists
watch(
  () => props.employeeData.id,
  (newId, oldId) => {
    // If employee just got selected (id changed from null/undefined to a value)
    // and there are no timeblocks for this employee, request one to be added
    if (newId && !oldId && props.timeblocks.length === 0) {
      nextTick(() => {
        emit('addTimeblock', -1);
      });
    }
  },
  { flush: 'post' }, // Ensure watch runs after all component updates
);

// On mount, check if employee has ID but no timeblocks
onMounted(() => {
  if (props.employeeData.id && props.timeblocks.length === 0) {
    nextTick(() => {
      emit('addTimeblock', -1);
    });
  }
});

const handleClearEmployee = () => {
  emit('clearEmployee');
};

const handleUpdateTimeblock = (index: number, value: TimeblockData) => {
  const updatedTimeblocks = [...props.timeblocks];
  updatedTimeblocks[index] = value;
  emit('update:timeblocks', updatedTimeblocks);
};

const handleRemoveTimeblock = (index: number) => {
  const updatedTimeblocks = [...props.timeblocks];
  updatedTimeblocks.splice(index, 1);
  emit('update:timeblocks', updatedTimeblocks);
};

const handleAddTimeblock = (afterIndex: number) => {
  emit('addTimeblock', afterIndex);
};

// Expose validate method that validates all child components
const validate = async () => {
  let isValid = true;
  // Validate employee select
  if (employeeSelectRef.value) {
    const selectValid = await employeeSelectRef.value.validate();
    isValid = isValid && selectValid;
  }

  // Validate all timeblocks
  for (const timeblockRef of timeblockRefs.value) {
    if (timeblockRef) {
      const timeblockValid = await timeblockRef.validate();
      isValid = isValid && timeblockValid;
    }
  }

  return isValid;
};

__expose({
  validate,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["employee", { 'employee--has-background': __props.hasBackground }])
  }, [
    _createVNode(AEmployeeSelect, {
      ref_key: "employeeSelectRef",
      ref: employeeSelectRef,
      modelValue: employeeDataLocal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((employeeDataLocal).value = $event)),
      "custom-filter": __props.customFilter,
      required: __props.required,
      onClearEmployee: handleClearEmployee
    }, null, 8, ["modelValue", "custom-filter", "required"]),
    (selectedEmployee.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.timeblocks, (timeblock, index) => {
            return (_openBlock(), _createBlock(AEmployeeTimeblock, {
              key: timeblock._clientId || timeblock.id || `timeblock-${index}`,
              ref_for: true,
              ref: (el) => setTimeblockRef(el, index),
              "model-value": timeblock,
              employee: selectedEmployee.value,
              "all-employee-timeblocks": __props.timeblocks,
              "all-timeblocks": allTimeblocksForCurrentEmployee.value,
              "deleted-timeblock-ids": __props.deletedTimeblockIds,
              "draft-timeblocks": __props.draftTimeblocks,
              "current-index": index,
              deletable: __props.timeblocks.length > 1,
              index: index,
              "onUpdate:modelValue": (val) => handleUpdateTimeblock(index, val),
              onAddTimeblock: ($event: any) => (handleAddTimeblock(index)),
              onRemoveTimeblock: ($event: any) => (handleRemoveTimeblock(index))
            }, null, 8, ["model-value", "employee", "all-employee-timeblocks", "all-timeblocks", "deleted-timeblock-ids", "draft-timeblocks", "current-index", "deletable", "index", "onUpdate:modelValue", "onAddTimeblock", "onRemoveTimeblock"]))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})